// Copied from kettle-platform workers/admin/ui/src/charts/WorldDots.tsx (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// Where requests come from: land as a faint dot matrix, each country with traffic as a
// glowing dot sized by its share (area ∝ count). Hover or focus a country for its name and
// number; the list beside the map carries the same numbers for screen readers.
import { useMemo, useState } from "react";
import { LAND, ORIGIN, PLACES, STEP } from "./world";

const W = 360;
const H = 136;
const names = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();
export const countryName = (iso: string) => {
  try {
    return names?.of(iso) ?? iso;
  } catch {
    return iso;
  }
};
/** 🇧🇷 from "BR" (regional indicator letters). */
export const flag = (iso: string) => (/^[A-Z]{2}$/.test(iso) ? String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "🏳");

const land = (() => {
  const out: [number, number][] = [];
  LAND.split("|").forEach((row, r) => {
    if (!row) return;
    for (const run of row.split(",")) {
      const [s, n] = run.split(".").map(Number) as [number, number];
      for (let i = 0; i < n; i++) out.push([(s + i) * STEP + STEP / 2, r * STEP + STEP / 2]);
    }
  });
  return out;
})();

export function WorldDots({ countries, label = "requests" }: { countries: { country: string; n: number }[]; label?: string }) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(1, ...countries.map((c) => c.n));
  const total = countries.reduce((a, c) => a + c.n, 0);
  const pts = useMemo(
    () =>
      countries
        .filter((c) => PLACES[c.country])
        .map((c) => {
          const [lon, lat] = PLACES[c.country]!;
          return { ...c, x: lon - ORIGIN.lon, y: ORIGIN.lat - lat, r: 1.4 + 6 * Math.sqrt(c.n / max) };
        })
        .sort((a, b) => b.n - a.n),
    [countries, max],
  );
  const shown = pts.find((p) => p.country === hover);
  return (
    <div className="world">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Where ${label} are: ${pts.slice(0, 5).map((p) => `${countryName(p.country)} ${p.n}`).join(", ")}`}>
        <defs>
          <radialGradient id="glow">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.55" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g className="land">{land.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={0.5} />)}</g>
        {[...pts].reverse().map((p, i) => (
          <g
            key={p.country}
            className={hover === p.country ? "place on" : "place"}
            tabIndex={0}
            onPointerEnter={() => setHover(p.country)}
            onPointerLeave={() => setHover(null)}
            onFocus={() => setHover(p.country)}
            onBlur={() => setHover(null)}
            aria-label={`${countryName(p.country)}: ${p.n} ${label}`}
            style={{ animationDelay: `${(i % 7) * 0.35}s` }}
          >
            <circle className="halo" cx={p.x} cy={p.y} r={p.r * 2.6} fill="url(#glow)" />
            <circle className="core" cx={p.x} cy={p.y} r={p.r} />
          </g>
        ))}
      </svg>
      {shown && (
        <div className="tip world-tip" role="status" style={{ left: `${(shown.x / W) * 100}%`, top: `${(shown.y / H) * 100}%` }}>
          <div className="tip-day">{flag(shown.country)} {countryName(shown.country)}</div>
          <div><span>{label}</span><b>{shown.n.toLocaleString("en-US")}</b></div>
          <div><span>share</span><b>{Math.round((100 * shown.n) / Math.max(1, total))}%</b></div>
        </div>
      )}
    </div>
  );
}
